import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Quick reference list showing recent wiki entries in compact format
 */
const QuickReferenceCards = ({ limit = 6, onSelectEntry }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecentEntries = async () => {
      try {
        setLoading(true);
        const allEntries = await wikiService.getAllWikiEntries();
        const sorted = allEntries.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || a.updatedAt || 0;
          const bTime = b.updatedAt?.toMillis?.() || b.updatedAt || 0;
          return bTime - aTime;
        });
        setEntries(sorted.slice(0, limit));
      } catch (error) {
        console.error('Error loading recent entries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecentEntries();
  }, [limit]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={30} sx={{ color: wikiTheme.primary }} />
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
          No entries yet. Create your first wiki entry!
        </Typography>
      </Box>
    );
  }

  const getTypeEmoji = (type) => {
    switch (type) {
      case 'location':
        return '📍';
      case 'person':
        return '👥';
      case 'item':
        return '⚔️';
      case 'monster':
        return '👹';
      default:
        return '📌';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'location':
        return '#42a5f5';
      case 'person':
        return '#66bb6a';
      case 'item':
        return '#ffa726';
      case 'monster':
        return '#e91e63';
      default:
        return wikiTheme.primary;
    }
  };

  return (
    <List sx={{ p: 0, width: '100%' }}>
      {entries.map((entry) => (
        <ListItem
          key={entry.id}
          disablePadding
          sx={{
            mb: 0.5,
            backgroundColor: wikiTheme.mediumGray,
            borderLeft: `3px solid ${getTypeColor(entry.type)}`,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#3d3d3d',
              transition: 'background-color 0.2s',
            },
          }}
        >
          <ListItemButton
            onClick={() => onSelectEntry?.(entry.id)}
            sx={{
              py: 1,
              px: 1.5,
              color: wikiTheme.secondary,
              fontFamily: wikiTheme.font,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '1.2rem',
                  flexShrink: 0,
                }}
              >
                {getTypeEmoji(entry.type)}
              </Typography>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: wikiTheme.secondary,
                    fontFamily: wikiTheme.font,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: wikiTheme.tertiary,
                    fontFamily: wikiTheme.font,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.description?.substring(0, 50).replace(/<[^>]*>/g, '') || 'No description'}...
                </Typography>
              </Box>
              <Chip
                label={entry.type.charAt(0).toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: getTypeColor(entry.type),
                  color: '#fff',
                  fontWeight: 'bold',
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  height: '20px',
                }}
              />
            </Box>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

export default QuickReferenceCards;
